import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";

import { analytics } from "../../analytics/analyticsEvents";
import { useGameStore } from "../../state/store";
import { useTutorialStore } from "../../state/tutorialStore";
import { computeBubblePosition, type BubblePos } from "./bubblePosition";
import { CoachmarkBubble } from "./CoachmarkBubble";
import { Spotlight } from "./Spotlight";
import { TUTORIAL_HINTS } from "./tutorialHints";
import { useTargetRect } from "./useTargetRect";
import styles from "./TutorialOverlay.module.css";

const HOLE_PADDING = 8;

/**
 * Shows each contextual hint once, the first time its screen is open while the
 * guided tutorial is not running.
 */
export function HintOverlay(): JSX.Element | null {
  const { i18n } = useLingui();
  const currentScreen = useGameStore((s) => s.currentScreen);
  const status = useTutorialStore((s) => s.status);
  const seenHints = useTutorialStore((s) => s.seenHints);
  const markHintSeen = useTutorialStore((s) => s.markHintSeen);
  const [bubblePos, setBubblePos] = useState<({ id: string } & BubblePos) | null>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const trackedRef = useRef<string | null>(null);

  const hint =
    status === "in_progress"
      ? undefined
      : TUTORIAL_HINTS.find((h) => h.screen === currentScreen && !seenHints.includes(h.id));
  const rect = useTargetRect(hint ? hint.targetId : null);
  const visible = Boolean(hint && rect);

  useLayoutEffect(() => {
    if (!hint || !rect) {
      return;
    }
    const el = bubbleRef.current;
    if (!el) {
      return;
    }
    const next = computeBubblePosition(rect, hint.pointerSide, el.offsetWidth, el.offsetHeight, {
      width: window.innerWidth,
      height: window.innerHeight,
    });
    setBubblePos((prev) =>
      prev && prev.id === hint.id && prev.top === next.top && prev.left === next.left
        ? prev
        : { id: hint.id, ...next },
    );
  }, [hint, rect]);

  useEffect(() => {
    if (!visible || !hint || trackedRef.current === hint.id) {
      return;
    }
    trackedRef.current = hint.id;
    analytics.tutorialHintViewed({ hint: hint.id });
  }, [visible, hint]);

  if (!hint || !rect) {
    return null;
  }

  return (
    <div className={styles.layer}>
      <Spotlight rect={rect} padding={HOLE_PADDING} dimActive={true} />
      <CoachmarkBubble
        title={i18n._(hint.title)}
        body={i18n._(hint.body)}
        buttonLabel={i18n._(msg`Понятно`)}
        style={{
          top: bubblePos?.id === hint.id ? bubblePos.top : undefined,
          left: bubblePos?.id === hint.id ? bubblePos.left : undefined,
          visibility: bubblePos?.id === hint.id ? "visible" : "hidden",
        }}
        bubbleRef={bubbleRef}
        onNext={() => markHintSeen(hint.id)}
      />
    </div>
  );
}
